/**
 * The application's own confirm / alert / prompt.
 *
 * The browser's `confirm()` was the one piece of this product drawn by
 * Chrome: a dark system box reading 「localhost:5173 の内容」, in the browser's
 * font, above everything - it looked like a warning from somewhere else, and
 * it is exactly the kind of box people dismiss by reflex.
 *
 * Same call shape as the browser's, so a call site stays one line, but
 * awaited: `if (!(await confirmDialog({...}))) return;`. Drawn with `Modal`,
 * so it is the native <dialog> underneath - focus trap, Escape, the backdrop
 * and inert page all come from the platform - and it stacks correctly over a
 * dialog that is already open (the CTA editor asks from inside its own).
 *
 * One host, mounted once in AppShell; requests are queued so two in a row are
 * answered in order rather than the second replacing the first.
 */
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Modal } from './Modal.tsx';

interface Request {
  kind: 'confirm' | 'alert' | 'prompt';
  title: string;
  message?: string | undefined;
  confirmLabel?: string | undefined;
  /** Marks an action that takes something away; the button says so in red. */
  danger?: boolean | undefined;
  defaultValue?: string | undefined;
  resolve: (value: boolean | string | null) => void;
}

let queue: Request[] = [];
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((listener) => listener());

function ask(request: Omit<Request, 'resolve'>): Promise<boolean | string | null> {
  return new Promise((resolve) => {
    queue = [...queue, { ...request, resolve }];
    emit();
  });
}

export const confirmDialog = (options: {
  title: string;
  message?: string;
  confirmLabel?: string;
  danger?: boolean;
}): Promise<boolean> => ask({ kind: 'confirm', ...options }).then((value) => value === true);

export const alertDialog = (options: { title: string; message?: string }): Promise<void> =>
  ask({ kind: 'alert', ...options }).then(() => undefined);

/** Resolves with the text, or null when cancelled - as `window.prompt` does. */
export const promptDialog = (options: {
  title: string;
  message?: string;
  defaultValue?: string;
  confirmLabel?: string;
}): Promise<string | null> =>
  ask({ kind: 'prompt', ...options }).then((value) => (typeof value === 'string' ? value : null));

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export function AppDialogHost() {
  const current = useSyncExternalStore(subscribe, () => queue[0] ?? null);
  const [value, setValue] = useState('');
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setValue(current?.defaultValue ?? '');
    if (current?.kind === 'prompt') requestAnimationFrame(() => input.current?.select());
  }, [current]);

  if (!current) return null;

  const answer = (result: boolean | string | null) => {
    queue = queue.slice(1);
    current.resolve(result);
    emit();
  };
  // Escape, the ✕ and the backdrop all mean "no" - the safe answer.
  const cancel = () => answer(current.kind === 'confirm' ? false : null);
  const accept = () => answer(current.kind === 'prompt' ? value : true);

  return (
    <Modal open onClose={cancel} title={current.title}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          accept();
        }}
      >
        {current.message && (
          <p className="appdialog__message">{current.message}</p>
        )}
        {current.kind === 'prompt' && (
          <input
            ref={input}
            className="appdialog__input"
            value={value}
            onChange={(event) => setValue(event.target.value)}
          />
        )}
        <div className="editpanel__actions">
          <button className={current.danger ? 'btn btn--danger' : 'btn'} type="submit" autoFocus>
            {current.confirmLabel ?? (current.kind === 'alert' ? '閉じる' : 'OK')}
          </button>
          {current.kind !== 'alert' && (
            <button className="btn btn--ghost" type="button" onClick={cancel}>
              キャンセル
            </button>
          )}
        </div>
      </form>
    </Modal>
  );
}
